import { addDays, toDateKey } from './planningDates';
import { sameEntityId, toFiniteNumber } from './planningNormalizers';

export const normalizeCapacityRow = (raw = {}) => {
  const capacityHours = Math.max(0, toFiniteNumber(raw.capacityHours ?? raw.capacity));
  const plannedHours = Math.max(0, toFiniteNumber(raw.plannedHours ?? raw.load));
  return {
    ...raw,
    userId: raw.userId,
    owner: raw.owner || '',
    date: raw.date,
    capacityHours,
    plannedHours,
    remainingHours: Math.max(0, capacityHours - plannedHours),
    overloadHours: Math.max(0, plannedHours - capacityHours),
    absenceHours: Math.max(0, toFiniteNumber(raw.absenceHours ?? (capacityHours === 0 ? raw.dailyCapacityHours : 0))),
    status: capacityHours === 0 ? 'absence' : plannedHours > capacityHours ? 'overloaded' : raw.status || 'ok',
  };
};

export const normalizePlanningCapacity = (rows = [], range = {}) => rows
  .map(normalizeCapacityRow)
  .filter((row) => (!range.start || row.date >= range.start) && (!range.end || row.date <= range.end));

export const calculatePlannedHours = (events = []) => events.reduce((sum, event) => sum + Math.max(0, toFiniteNumber(event.durationMinutes)) / 60, 0);
export const calculateAvailableCapacity = (capacity = []) => Math.max(0, capacity.reduce((sum, row) => sum + row.capacityHours, 0) - capacity.reduce((sum, row) => sum + row.plannedHours, 0));
export const calculateOccupancyRate = (plannedHours, capacityHours) => capacityHours > 0 ? Math.round((plannedHours / capacityHours) * 100) : null;
export const calculateUpcomingDeadlines = (events = [], today = new Date()) => {
  const start = toDateKey(today); const end = toDateKey(addDays(today, 7));
  return events.filter((event) => event.elementType === 'milestone' && event.endDate >= start && event.endDate <= end && !['completed', 'cancelled'].includes(event.status)).length;
};
export const calculatePendingSyncCount = (events = []) => events.filter((event) => ['not_synced', 'pending', 'error', 'external_only'].includes(event.syncStatus)).length;

export const calculatePlanningSummary = (events = [], capacity = []) => {
  const plannedHours = calculatePlannedHours(events);
  const capacityHours = capacity.reduce((sum, row) => sum + row.capacityHours, 0);
  const assignedResourceIds = new Set(events.map((event) => event.resourceId).filter(Boolean).map(String));
  const availableResourceIds = new Set(capacity.filter((row) => row.capacityHours > 0).map((row) => String(row.userId)));
  return {
    plannedHours,
    capacityHours,
    availableCapacity: calculateAvailableCapacity(capacity),
    overloadHours: capacity.reduce((sum, row) => sum + row.overloadHours, 0),
    occupancyRate: calculateOccupancyRate(plannedHours, capacityHours),
    assignedResources: assignedResourceIds.size,
    availableResources: availableResourceIds.size,
    unassignedEvents: events.filter((event) => !event.resourceId).length,
    upcomingDeadlines: calculateUpcomingDeadlines(events),
    pendingSync: calculatePendingSyncCount(events),
  };
};

export const findCapacity = (capacity, resourceId, date) => capacity.find((row) => sameEntityId(row.userId, resourceId) && row.date === date);
